import { W3cddlData } from '@sparknz/tracking';

import { getBroadbandAttributes } from 'journey/checkout/utils/tracking.utils';
import { Channel } from 'shared/hooks';
import { TrackingCart } from 'shared/types/cart';
import { isPromotion } from 'shared/utils/tracking/buildTrackingCartData.utils';
import {
  buildCartProductData,
  getFrequencyCostSummary,
} from 'shared/utils/tracking/buildTrackingProductData.utils';
import {
  getConnectionTypeValue,
  getFormData,
} from 'shared/utils/tracking/connectionType.utils';
import { DEFAULT_TRACKING_PRICE } from 'shared/utils/tracking/tracking.constants';
import {
  DELIVERY_ADDRESS_FIELD_NAME,
  DELIVERY_METHOD_FIELD_NAME,
  DELIVERY_SECTION_ID,
  DELIVERY_STEP_PATH,
  PAYMENT_METHOD_FIELD_NAME,
  PAYMENT_SAVE_CARD_FIELD_NAME,
  PAYMENT_SECTION_ID,
  PAYMENT_STEP_PATH,
  SETUP_SERVICE_PROVIDER_FIELD_NAME,
} from '../../../constants';
import {
  CheckoutCartBundle,
  CheckoutData,
  FormData,
  StockInfo,
} from '../../../types';
import { extractCheckoutDataStep } from '../../../utils/extractCheckoutDataStep.utils';

// Will export this directly from tracking lib in future
type Transaction = W3cddlData['transaction'][0];
type PaymentMethodType = Transaction['attributes']['paymentMethod'];

const GIFT_CARD_PAYMENT_METHOD: PaymentMethodType = 'GiftCard';
const CARD_PAYMENT_METHOD: PaymentMethodType = 'CC';

const SHIPPING_VALUE_LOOKUP_RECORD: Record<string, TrackingShippingMethod> = {
  'Standard delivery - Free': 'Standard delivery',
  'Extended delivery - Free': 'Extended delivery',
  'Click & Collect - Free': 'Click & Collect',
};

interface AddressParts {
  suburb: string;
  city: string;
  postalCode: string;
}

function getAddressParts(address: string): AddressParts {
  if (address) {
    const [, suburb, lastSection] = address?.split(', ');

    if (lastSection) {
      const [city, postalCode] = lastSection.split(' ');
      return { city, postalCode, suburb };
    }
  }

  return { city: '', postalCode: '', suburb: '' };
}

function transformPaymentMethod(
  paymentMethod: string,
): 'CC' | 'ACCOUNT' | 'GiftCard' {
  if (paymentMethod === 'Credit/Debit Card') {
    return 'CC';
  }
  if (paymentMethod === 'Gift Card') {
    return 'GiftCard';
  }
  return 'ACCOUNT';
}

type TrackingShippingMethod =
  | 'Standard delivery'
  | 'Extended delivery'
  | 'Click & Collect';

function transformShippingMethod(
  shippingMethod: string,
): TrackingShippingMethod {
  return SHIPPING_VALUE_LOOKUP_RECORD[shippingMethod] ?? null;
}

export type CreditCheckOutcome =
  | 'PASS'
  | 'BIOMETRICS_FAILED'
  | 'CREDIT_CHECK_REFERRED'
  | 'CREDIT_CHECK_FAILED';

interface BuildCreditCheckInfoParameter {
  creditCheckOutcome: CreditCheckOutcome;
  upfrontTotal: string;
}

export function buildCreditCheckInfo({
  creditCheckOutcome,
  upfrontTotal,
}: BuildCreditCheckInfoParameter): Transaction['attributes']['creditCheckInfo'] {
  if (!creditCheckOutcome) {
    return null;
  }

  if (creditCheckOutcome === 'CREDIT_CHECK_FAILED') {
    return {
      creditCheckResult: 'Rejected',
      paymentAmount: upfrontTotal,
    };
  }

  if (creditCheckOutcome === 'CREDIT_CHECK_REFERRED') {
    return {
      creditCheckResult: 'Referred',
      paymentAmount: upfrontTotal,
    };
  }

  if (creditCheckOutcome === 'BIOMETRICS_FAILED') {
    return {
      creditCheckResult: 'Passed',
      paymentAmount: upfrontTotal,
      biometricCheckResult: 'Needs to be finalised',
    };
  }

  return {
    creditCheckResult: 'Passed',
    paymentAmount: upfrontTotal,
    biometricCheckResult: 'Passed',
  };
}

interface BuildTransactionItemDataParameter {
  cart: TrackingCart;
  isBusiness: boolean;
  checkoutData: CheckoutData;
}
export const buildTransactionItemData = ({
  cart,
  isBusiness,
  checkoutData,
}: BuildTransactionItemDataParameter) => {
  return cart.bundles
    .map((bundle) => {
      const formData: FormData = getFormData(checkoutData, bundle.bundleId);

      const connectionType = getConnectionTypeValue({
        bundle: bundle as CheckoutCartBundle,
        formData,
      });

      const currentProvider =
        (formData[SETUP_SERVICE_PROVIDER_FIELD_NAME] as string) || 'Spark';

      const broadbandAttributes = getBroadbandAttributes(formData);

      return buildCartProductData({
        bundle,
        summaries: cart.summaries,
        isBusiness,
        connectionType,
        currentProvider,
        broadbandAttributes,
      });
    })
    .flat();
};

interface BuildTrackingTransactionDataParameter {
  cart: TrackingCart;
  channel: Channel;
  checkoutData: CheckoutData;
  creditCheckOutcome?: CreditCheckOutcome;
  isBusiness: boolean;
  orderNumber: string;
  sparkId: string | null;
  isSplitPayment: boolean;
  stockInfo: StockInfo;
}

export function buildTrackingTransactionData({
  cart,
  channel,
  checkoutData,
  creditCheckOutcome,
  isBusiness,
  orderNumber,
  sparkId,
  isSplitPayment = false,
  stockInfo,
}: BuildTrackingTransactionDataParameter): Transaction {
  const { sections } = checkoutData;
  const { auth, bundles } = cart;
  const isExistingCustomer = !auth?.isGuest;

  const deliveryCheckoutDataStep = extractCheckoutDataStep({
    sections,
    sectionId: DELIVERY_SECTION_ID,
    stepPath: DELIVERY_STEP_PATH,
  });

  const shippingAddress =
    deliveryCheckoutDataStep?.entries?.find(
      (entry) => entry.name === DELIVERY_ADDRESS_FIELD_NAME,
    )?.value ?? '';

  const { suburb, city, postalCode } = getAddressParts(shippingAddress);

  const shippingMethod =
    deliveryCheckoutDataStep?.entries?.find(
      (entry) => entry.name === DELIVERY_METHOD_FIELD_NAME,
    )?.value ?? '';

  const paymentCheckoutDataStep = extractCheckoutDataStep({
    sections,
    sectionId: PAYMENT_SECTION_ID,
    stepPath: PAYMENT_STEP_PATH,
  });

  const checkoutDataPaymentMethodValue =
    paymentCheckoutDataStep?.entries?.find(
      (entry) => entry.name === PAYMENT_METHOD_FIELD_NAME,
    )?.value ?? '';

  const paymentMethod = transformPaymentMethod(checkoutDataPaymentMethodValue);

  const isSaveCardChecked =
    paymentCheckoutDataStep?.entries?.find(
      (entry) => entry.name === PAYMENT_SAVE_CARD_FIELD_NAME,
    )?.value === 'true' ?? false;

  const cartTotalOneOffCost = String(
    cart.summaries.find(
      (summary) => summary.billingFrequency === 'ONE_OFF_TOTAL',
    )?.total ?? '0.00',
  );

  const upfrontTotal = String(
    cart.summaries.find((summary) => summary.billingFrequency === 'UP_FRONT')
      ?.total ?? '0.00',
  );

  const cartTotalRecurringCost = String(
    cart.summaries.find((summary) => summary.billingFrequency === 'MONTHLY')
      ?.total ?? '0.00',
  );

  const isChangePlan = bundles?.some((bundle) => {
    return bundle?.processContext === 'CHANGE_OFFER';
  });

  // There is a type mismatch between the graphql type and channel
  let baseStore;
  if (channel === 'businessshop' || channel === 'personalshop') {
    baseStore = channel;
  }

  const isGiftWithPurchase = isPromotion(cart?.bundles);

  const attributePaymentMethod = isSplitPayment
    ? GIFT_CARD_PAYMENT_METHOD
    : paymentMethod;

  const oneOffCostPaymentMethod = isSplitPayment
    ? ([GIFT_CARD_PAYMENT_METHOD, CARD_PAYMENT_METHOD].join(
        ',',
      ) as PaymentMethodType)
    : paymentMethod;

  const oneOffCostSummary = getFrequencyCostSummary(
    cart.summaries,
    'ONE_OFF_TOTAL',
  );

  const oneOffDiscount = oneOffCostSummary.discount
    ? `${oneOffCostSummary.discount}`
    : DEFAULT_TRACKING_PRICE;

  const recurringCostPaymentMethod = isSplitPayment
    ? CARD_PAYMENT_METHOD
    : paymentMethod;

  const monthlyCostSummary = getFrequencyCostSummary(cart.summaries, 'MONTHLY');

  const monthlyDiscount = monthlyCostSummary.discount
    ? `${monthlyCostSummary.discount}`
    : DEFAULT_TRACKING_PRICE;

  return {
    transactionID: orderNumber,
    attributes: {
      accountCredit: '0.00',
      paymentMethod: attributePaymentMethod,
      oneOffCostPaymentMethod,
      recurringCostPaymentMethod,
      isSaveCreditCard: isSaveCardChecked,
      baseStore,
      cartTotalOneOffCost,
      cartTotalOneOffCostDiscount: oneOffDiscount,
      cartTotalRecurringCost,
      cartTotalRecurringCostDiscount: monthlyDiscount,
      isExistingCustomer,
      isGiftWithPurchase,
      numOfBundles: String(cart.bundles.length),
      shippingAddressSuburb: suburb,
      isSplitPayment,
      retailStore: '',
      stockInfo,
      creditCheckInfo: buildCreditCheckInfo({
        creditCheckOutcome,
        upfrontTotal,
      }),
      isChangePlan,
    },
    profile: {
      profileInfo: {
        profileID: sparkId,
      },
      shippingAddress: {
        city,
        postalCode,
        country: 'New Zealand',
      },
    },
    total: {
      basePrice:
        Number(cartTotalOneOffCost) > 0
          ? cartTotalOneOffCost
          : cartTotalRecurringCost,
      currency: 'NZD',
      voucherDiscount: '0.00',
      voucherCode: '',
      shippingMethod: transformShippingMethod(shippingMethod),
      transactionTotal:
        Number(cartTotalOneOffCost) > 0
          ? cartTotalOneOffCost
          : cartTotalRecurringCost,
      shipping: '0.00',
    },
    item: buildTransactionItemData({ cart, isBusiness, checkoutData }),
  };
}
